/**
 * lib/routeAccess.ts
 * ------------------------------------------------------------------
 * WHETHER A USER MAY OPEN A SCREEN AT ALL.
 *
 * Until 5 September 2026 this was decided in one place only — the sidebar.
 * NAV entries carried a `gate` and the sidebar hid the ones a user could not
 * use. Nothing checked the gate again when a screen actually rendered, so
 * `AppContent.render()` was a bare `switch (active)` that would render
 * anything the URL asked for.
 *
 * A hidden link is not an access control. The hash is user-editable, the
 * back button replays history, and a hardcoded `onBack` can send someone
 * somewhere the sidebar would never have offered — which is exactly what
 * happened: a teacher opening a pupil from their own class and pressing
 * Back landed on the whole-school student roster, with every pupil's record
 * in front of them and edit controls on the page.
 *
 * So the gate moves here, it covers EVERY route including the deep-link
 * ones that never appear in the sidebar, and render() consults it.
 *
 * EXHAUSTIVE ON PURPOSE. `Record<TabId, …>` means a new screen does not
 * compile until somebody says who may open it. The failure mode of a
 * default — "unknown route, allow it" — is a screen nobody remembered to
 * protect; the failure mode of this one is a compiler error.
 *
 * `null` means genuinely open to any signed-in user. There are four, and
 * each is a screen about the viewer themselves.
 * ------------------------------------------------------------------ */
import type { Permissions } from '@/lib/permissions';
import type { TabId } from '@/components/Shell';

/**
 * The permission each screen needs, or null for "any signed-in user".
 *
 * Mirrors the `gate` on each NAV entry — routeAccess.test.ts asserts the two
 * agree for every entry, so the sidebar and the router cannot drift into
 * disagreeing about who may see what.
 */
export const ROUTE_GATE: Record<TabId, keyof Permissions | null> = {
  /* --- about the viewer themselves ---------------------------------- */
  home: null,
  account: null,
  myPayHr: null,
  progress: null,

  /* --- parent portal ------------------------------------------------- */
  parentPortal: 'canAccessParentPortal',
  parentFees: 'canAccessParentPortal',
  parentMessages: 'canAccessParentPortal',

  /* --- setup / roster ------------------------------------------------ */
  setupHub: 'canAccessSetup',
  admissions: 'canAccessAdmissions',
  textbookClearance: 'canAccessAdmissions',
  teachers: 'canManageTeachers',
  students: 'canManageStudents',
  alumni: 'canManageStudents',
  teacherSwap: 'canManageStudents',
  families: 'canManageFamilies',
  subjects: 'canManageSubjects',
  parentOnboarding: 'canManageParentSettings',
  classes: 'canManageClasses',

  /* --- DEEP-LINK ROUTES, which never appear in the sidebar -----------
   *
   * These are the ones that had no gate of any kind, because the sidebar
   * was the only gate and they are not in it. A profile route reached by
   * URL is still a profile route.
   *
   * studentProfile is deliberately NOT canManageStudents: a class teacher
   * must be able to open a pupil in their own class. The route lets them
   * through and StudentProfile itself decides WHICH pupil and how much of
   * them, via canViewStudentProfile — a per-pupil question this map cannot
   * answer. */
  studentProfile: 'canViewLiveAcademics',
  teacherProfile: 'canManageTeachers',
  staffProfile: 'canAccessHR',
  headInsight: 'canViewProgress',
  assessmentConfig: 'canManageTerms',
  hrPayRun: 'canAccessPayroll',
  hrDeductionReports: 'canAccessPayroll',

  /* --- daily academic work ------------------------------------------- */
  attendance: 'canEnterClassTeacherComment',
  marks: 'canEnterMarks',
  classComment: 'canEnterClassTeacherComment',
  hodComment: 'canEnterHodComment',
  commentReview: 'canViewCommentReview',
  flags: 'canRaiseFlags',
  discipline: 'canManageDiscipline',
  achievements: 'canManageAchievements',

  /* --- teaching + study portal (from Al-Nur) ------------------------- */
  teachingLessons: 'canViewStudyProgress',
  homework: 'canViewStudyProgress',
  schemes: 'canViewStudyProgress',
  lessonPlans: 'canViewStudyProgress',
  markbook: 'canViewStudyProgress',
  textbooks: 'canViewStudyProgress',
  studyProgress: 'canViewStudyProgress',
  marking: 'canViewStudyProgress',
  studyLibrary: 'canViewStudyProgress',
  practicals: 'canViewStudyProgress',
  syllabusEditor: 'canManageCurriculum',
  nako: 'canOpenNako',
  /* The pupil's own portal: the student role's single grant. */
  studyPortal: 'canAccessStudentPortal',

  /* --- analysis and reports ------------------------------------------ */
  marksheet: 'canViewProgress',
  teacherAnalysis: 'canViewProgress',
  marksCompliance: 'canViewProgress',
  exams: 'canManageExamTimetables',
  examTimetable: 'canViewExams',
  internalExams: 'canManageExamTimetables',
  examRegistration: 'canManageExamTimetables',
  examTimetables: 'canManageExamTimetables',
  examTools: 'canManageExams',
  invigilation: 'canManageExamTimetables',
  myInvigilation: 'canViewExams',
  examRegisters: 'canViewExams',
  examSeating: 'canViewExams',
  examRoom: 'canViewExams',
  reports: 'canGenerateReports',
  viewReports: 'canViewLiveAcademics',
  bulkSend: 'canBulkSendReports',
  myBoarders: 'canViewBoarderReports',

  /* --- academic administration ---------------------------------------- */
  terms: 'canManageTerms',
  promotion: 'canManageTerms',
  bulkComments: 'canBulkImport',
  templates: 'canManageTerms',

  /* --- system ---------------------------------------------------------- */
  users: 'canManageTeacherAccounts',
  audit: 'canViewAudit',
  activity: 'canViewActivityLog',
  bulkImport: 'canBulkImport',

  /* --- school settings and communications ------------------------------ */
  parentSettings: 'canManageParentSettings',
  schoolComms: 'canManageParentSettings',
  termDates: 'canManageTerms',
  teachingDays: 'canManageTerms',
  myTimetable: 'canViewStudyProgress',
  lessonRegister: 'canViewStudyProgress',
  cover: 'canManageCover',
  staffing: 'canEditStaffing',
  subjectChoices: 'canEditStaffing',
  announcements: 'canManageTerms',
  publicHolidays: 'canAccessHR',
  schoolMailbox: 'canReadSchoolMail',
  photoManagement: 'canReviewPupilPhotos',
  parentContacts: 'canCollectParentContacts',
  parentNotices: 'canManageTerms',
  messageParents: 'canMessageParents',

  /* --- HR ---------------------------------------------------------------- */
  hrStaff: 'canAccessHR',
  hrCompliance: 'canAccessHR',
  hrClocking: 'canViewClocking',
  hrClockingImport: 'canImportClocking',
  hrLeave: 'canAccessHR',
  hrContracts: 'canAccessHR',
  hrAppraisals: 'canAccessHR',

  /* --- payroll ------------------------------------------------------------ */
  hrPayroll: 'canAccessPayroll',
  hrSeverance: 'canAccessPayroll',
  hrLoans: 'canAccessPayroll',
  hrMedicalAid: 'canAccessPayroll',
  hrPaymentDetails: 'canAccessPayroll',
  hrTaxTables: 'canManageTaxTables',
  hrBurs: 'canAccessPayroll',

  /* --- finance ------------------------------------------------------------- */
  financeOverview: 'canViewFinanceTotals',
  financeFees: 'canManageFinanceConfig',
  financeSpecialCases: 'canAccessFinance',
  financeSiblingDiscounts: 'canAccessFinance',
  financeBilling: 'canViewFinanceTotals',
  financeInvoice: 'canAccessFinance',
  textbookCharges: 'canAccessFinance',
  financeExamFees: 'canAccessFinance',
  financePayments: 'canAccessFinance',
  financeUnidentified: 'canAccessFinance',
  financeCashUp: 'canAccessFinance',
  financeStatements: 'canViewFinanceTotals',
  financeDebtors: 'canViewFinanceTotals',
  financeArrangements: 'canAccessFinance',
  financeAdjustments: 'canAccessFinance',
  financeDiscounts: 'canAccessFinance',
  financeArrears: 'canViewFinanceTotals',
  financeAgeAnalysis: 'canImportPastel',
  financeReconcile: 'canAccessFinance',
  financeJournals: 'canViewGeneralLedger',
  financeGeneralLedger: 'canViewGeneralLedger',
  financeOpeningBalances: 'canAccessFinance',
  financePeriodEnd: 'canAccessFinance',
  financeTrialBalance: 'canViewGeneralLedger',
  financeIncomeExp: 'canViewFinanceTotals',
  financeBalanceSheet: 'canViewFinanceTotals',
  financeCashFlow: 'canViewFinanceTotals',
  financeControlAccounts: 'canViewGeneralLedger',
  financeAnomalies: 'canViewGeneralLedger',
  financeSuppliers: 'canManageSuppliers',
  financeRequisitions: null,
  financePurchaseOrders: 'canRaiseVouchers',
  financePaymentBatches: 'canReleasePayments',
  financeFeeStatus: 'canViewFinanceTotals',
  financeAnalysis: 'canViewFinanceTotals',
  financeAssets: 'canViewFinanceTotals',
  financeBudgets: 'canViewFinanceTotals',
  financeHousekeeping: 'canViewFinanceTotals',
  financeFinancialYear: 'canPostJournals',
  financeAuditPack: 'canViewFinanceTotals',
  financeTaxVat: 'canViewFinanceTotals',
  financeDetailedLedger: 'canViewFinanceTotals',
  financePartyJournals: 'canPostJournals',
  financeDocuments: 'canViewFinanceTotals',
  financeBankProof: 'canViewFinanceTotals',
  financeCashBook: 'canPostJournals',
  financeStandingBills: 'canRaiseVouchers',
  financePettyCash: 'canRaiseVouchers',
  financeRemittance: 'canReleasePayments',
  financeCreditorsAging: 'canViewFinanceTotals',
  financeSupplierStatements: 'canViewFinanceTotals',
  financeVouchers: 'canRaiseVouchers',
  financeChartOfAccounts: 'canManageChartOfAccounts',
};

/** May this user open this screen? */
export function canOpenRoute(tab: TabId, permissions: Permissions): boolean {
  const gate = ROUTE_GATE[tab];
  // An unrecognised tab is refused rather than allowed. A typo in a hash
  // should land on "not available", never on whatever renders by default.
  if (gate === undefined) return false;
  return gate === null || permissions[gate] === true;
}

/**
 * Where "back" should go from a deep-link route.
 *
 * A hardcoded back target is how the roster leak happened: StudentProfile
 * sent everyone to the whole-school student list, including teachers who
 * had arrived from their own class and had no business there. Back now goes
 * somewhere the user is actually allowed to be, preferring the screen they
 * most likely came from.
 */
export function fallbackRoute(preferred: TabId[], permissions: Permissions): TabId {
  for (const t of preferred) if (canOpenRoute(t, permissions)) return t;
  return 'home';
}
